import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Minimize2, Phone, MessageCircle, AlertOctagon, MapPin, ArrowLeft } from "lucide-react";

const SPEED_OPTIONS = {
  slow: { label: "Slow", value: 0.32 },
  medium: { label: "Medium", value: 0.55 },
  fast: { label: "Fast", value: 0.85 }
};
const DURATION_OPTIONS = {
  short: { label: "1 min", seconds: 60 },
  mid: { label: "3 min", seconds: 180 },
  long: { label: "5 min", seconds: 300 }
};

const EMDRBall = ({ open, onClose }) => {
  const containerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, dir: 1 });
  const [showInstructions, setShowInstructions] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [speedKey, setSpeedKey] = useState(() => {
    try { return localStorage.getItem('emdrSpeed') || 'medium'; }
    catch { return 'medium'; }
  });
  const [durationKey, setDurationKey] = useState(() => {
    try { return localStorage.getItem('emdrDuration') || 'mid'; }
    catch { return 'mid'; }
  });
  const [secondsLeft, setSecondsLeft] = useState(DURATION_OPTIONS.mid.seconds);
  const [stage, setStage] = useState('running'); // 'running' | 'completed' | 'help' | 'resources'
  const [zipCode, setZipCode] = useState('');
  const [zipSubmitted, setZipSubmitted] = useState(false);

  useEffect(() => { try { localStorage.setItem('emdrSpeed', speedKey); } catch {} }, [speedKey]);
  useEffect(() => { try { localStorage.setItem('emdrDuration', durationKey); } catch {} }, [durationKey]);

  // Audio context init on open
  useEffect(() => {
    if (!open) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !audioCtxRef.current) audioCtxRef.current = new Ctx();
    } catch { /* ignore */ }
  }, [open]);

  // Reset state on each open
  useEffect(() => {
    if (open) {
      setPos({ x: 0, dir: 1 });
      setShowInstructions(true);
      setStage('running');
      setZipCode('');
      setZipSubmitted(false);
      setSecondsLeft(DURATION_OPTIONS[durationKey].seconds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset countdown when duration changes mid-session
  useEffect(() => {
    if (stage === 'running') setSecondsLeft(DURATION_OPTIONS[durationKey].seconds);
  }, [durationKey, stage]);

  // 10s instruction fade
  useEffect(() => {
    if (!open || stage !== 'running') return;
    setShowInstructions(true);
    const t = setTimeout(() => setShowInstructions(false), 10000);
    return () => clearTimeout(t);
  }, [open, stage]);

  // Countdown
  useEffect(() => {
    if (!open || stage !== 'running') return;
    if (secondsLeft <= 0) {
      setStage('completed');
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, open, stage]);

  // Animation loop with current speed
  useEffect(() => {
    if (!open || stage !== 'running') return;
    let raf;
    let lastTs = null;
    const tick = (ts) => {
      if (lastTs == null) lastTs = ts;
      const dt = Math.min(50, ts - lastTs);
      lastTs = ts;
      const speed = SPEED_OPTIONS[speedKey].value;
      setPos((prev) => {
        const w = containerRef.current?.clientWidth || 800;
        const ballSize = Math.min(64, w * 0.08);
        const maxX = w - ballSize;
        let nx = prev.x + prev.dir * speed * dt;
        let dir = prev.dir;
        let bounced = false;
        if (nx >= maxX) { nx = maxX; dir = -1; bounced = true; }
        if (nx <= 0)    { nx = 0;    dir = 1;  bounced = true; }
        if (bounced) playBounce();
        return { x: nx, dir };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, stage, speedKey]);

  const playBounce = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      // Lowered ~1 octave for a deeper, calmer bounce — less startling for trauma users
      osc.frequency.setValueAtTime(220, now);
      osc.frequency.exponentialRampToValueAtTime(90, now + 0.18);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    } catch { /* ignore */ }
  };

  const handleFullscreen = async () => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    try {
      if (!document.fullscreenElement) { await el.requestFullscreen?.(); setFullscreen(true); }
      else { await document.exitFullscreen?.(); setFullscreen(false); }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const handleClose = () => { setFullscreen(false); onClose(); };
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[400] flex items-center justify-center px-4 py-6"
          style={{ background: "rgba(8, 6, 18, 0.92)", backdropFilter: "blur(10px)" }}
          data-testid="emdr-overlay"
        >
          <div
            className="relative w-full"
            style={{
              maxWidth: fullscreen ? "100vw" : "min(960px, 92vw)",
              aspectRatio: "16 / 9"
            }}
          >
            <div
              ref={containerRef}
              className="relative w-full h-full rounded-2xl overflow-hidden"
              style={{
                background: "radial-gradient(circle at 30% 30%, #1a2840, #0a0d1c 60%), linear-gradient(180deg, #0a0d1c, #050610)",
                border: "1.5px solid rgba(123,94,167,0.4)",
                boxShadow: "0 30px 100px rgba(79,55,139,0.4)"
              }}
            >
              {/* Soft starfield */}
              <div className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(2px 2px at 20% 20%, rgba(255,255,255,0.4), transparent), radial-gradient(1px 1px at 70% 35%, rgba(255,255,255,0.3), transparent), radial-gradient(2px 2px at 45% 70%, rgba(255,255,255,0.25), transparent), radial-gradient(1px 1px at 85% 80%, rgba(255,255,255,0.3), transparent)",
                  opacity: 0.6
                }}
                aria-hidden="true"
              />

              {/* RUNNING STAGE */}
              {stage === 'running' && (
                <>
                  {/* Instruction banner — LEFT side so the right-corner controls don't cover it */}
                  <AnimatePresence>
                    {showInstructions && (
                      <motion.div
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.6 }}
                        className="absolute top-3 left-3 px-4 py-2 rounded-full text-xs font-semibold max-w-[55%]"
                        style={{
                          background: "rgba(255, 248, 220, 0.92)",
                          color: "#3D2A0E",
                          backdropFilter: "blur(6px)",
                          fontFamily: "'Nunito', sans-serif",
                          boxShadow: "0 6px 22px rgba(0,0,0,0.4)"
                        }}
                        data-testid="emdr-instructions"
                      >
                        Follow the ball with your eyes while you think about your trauma.
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Bouncing blueberry */}
                  <div className="absolute top-1/2"
                    style={{
                      left: `${pos.x}px`,
                      transform: "translateY(-50%)",
                      width: "min(64px, 8vw)", height: "min(64px, 8vw)",
                      willChange: "left"
                    }}
                    aria-hidden="true"
                  >
                    <div className="w-full h-full rounded-full"
                      style={{
                        background: "radial-gradient(circle at 30% 30%, #8aa8ff 0%, #4F378B 45%, #2A1B5C 100%)",
                        boxShadow: "0 0 30px rgba(123,94,167,0.85), 0 0 60px rgba(79,55,139,0.6), inset 0 -4px 8px rgba(0,0,0,0.4), inset 0 3px 6px rgba(255,255,255,0.4)"
                      }}
                    />
                    <div className="absolute"
                      style={{
                        top: "8%", left: "55%", width: "10%", height: "10%", borderRadius: "50%",
                        background: "#1a0e3a", boxShadow: "inset 0 -1px 2px rgba(0,0,0,0.6)"
                      }}
                    />
                  </div>

                  {/* Bottom controls: speed + duration + countdown */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 flex-wrap justify-center" data-testid="emdr-controls">
                    <SegmentControl
                      label="Speed"
                      options={SPEED_OPTIONS}
                      activeKey={speedKey}
                      onChange={setSpeedKey}
                      testIdPrefix="emdr-speed"
                    />
                    <SegmentControl
                      label="Length"
                      options={DURATION_OPTIONS}
                      activeKey={durationKey}
                      onChange={setDurationKey}
                      testIdPrefix="emdr-duration"
                    />
                    <div
                      className="px-3 py-1 rounded-full text-[10px] font-bold"
                      style={{ background: 'rgba(212,165,116,0.18)', color: '#D4A574', fontFamily: "'Nunito', sans-serif" }}
                      data-testid="emdr-countdown"
                    >
                      {formatTime(secondsLeft)}
                    </div>
                  </div>
                </>
              )}

              {/* COMPLETED STAGE — feel better / need help */}
              {stage === 'completed' && (
                <CenterPanel>
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>
                    How are you feeling now?
                  </h3>
                  <p className="text-xs mb-5 italic" style={{ color: '#B8A8D8' }}>
                    Take a slow breath before answering.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <button
                      onClick={handleClose}
                      className="flex-1 py-3 rounded-full font-bold text-sm"
                      style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}
                      data-testid="emdr-feel-better"
                    >
                      I Feel Better
                    </button>
                    <button
                      onClick={() => setStage('help')}
                      className="flex-1 py-3 rounded-full font-bold text-sm"
                      style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)', color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}
                      data-testid="emdr-need-help"
                    >
                      Need Additional Help
                    </button>
                  </div>
                </CenterPanel>
              )}

              {/* HELP STAGE — ZIP request before showing resources */}
              {stage === 'help' && !zipSubmitted && (
                <CenterPanel>
                  <button
                    onClick={() => setStage('completed')}
                    className="absolute top-3 left-3 flex items-center gap-1 text-xs hover:opacity-80"
                    style={{ color: '#B8A8D8' }}
                    data-testid="emdr-help-back"
                  >
                    <ArrowLeft size={12} /> Back
                  </button>
                  <MapPin size={28} style={{ color: '#D4A574', marginBottom: 8 }} />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>
                    Find local support
                  </h3>
                  <p className="text-xs mb-4" style={{ color: '#B8A8D8' }}>
                    Enter your U.S. ZIP code &mdash; we'll surface Primordeia practitioners and licensed female psychologists near you, plus 24/7 national crisis lines.
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="ZIP code"
                    className="w-full max-w-[200px] px-4 py-2.5 rounded-full text-sm text-center mb-3"
                    style={{
                      background: 'rgba(255,255,255,0.08)', color: '#FFF',
                      border: '1px solid rgba(212,165,116,0.4)', fontFamily: "'Nunito', sans-serif"
                    }}
                    data-testid="emdr-zip-input"
                  />
                  <button
                    onClick={() => setZipSubmitted(true)}
                    className="px-6 py-2 rounded-full text-xs font-bold"
                    style={{ background: 'linear-gradient(135deg, #D4A574, #B8895A)', color: '#3D2A0E', fontFamily: "'Fredoka', sans-serif" }}
                    data-testid="emdr-zip-submit"
                  >
                    Show Support Options
                  </button>
                </CenterPanel>
              )}

              {/* HELP STAGE — resources after ZIP submitted */}
              {stage === 'help' && zipSubmitted && (
                <CrisisResourcesPanel zipCode={zipCode} onBack={() => setZipSubmitted(false)} onClose={handleClose} />
              )}

              {/* Top-right controls: only during running */}
              {stage === 'running' && (
                <div className="absolute top-3 right-3 flex gap-2 z-10">
                  <button onClick={handleFullscreen}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/15 transition"
                    style={{ background: "rgba(255,255,255,0.08)", color: "#E8E0F8", backdropFilter: "blur(6px)" }}
                    aria-label="Toggle fullscreen" data-testid="emdr-fullscreen"
                  >
                    {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                  </button>
                  <button onClick={handleClose}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/15 transition"
                    style={{ background: "rgba(255,255,255,0.08)", color: "#E8E0F8", backdropFilter: "blur(6px)" }}
                    aria-label="Close trauma chill zone" data-testid="emdr-close"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// --- helpers ---

const SegmentControl = ({ label, options, activeKey, onChange, testIdPrefix }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-full"
    style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(6px)', border: '1px solid rgba(123,94,167,0.3)' }}
  >
    <span className="text-[9px] font-bold uppercase tracking-widest pl-1 pr-1"
      style={{ color: '#8B7DA8', fontFamily: "'Cinzel', serif" }}>
      {label}
    </span>
    {Object.entries(options).map(([k, v]) => {
      const isActive = activeKey === k;
      return (
        <button key={k} type="button" onClick={() => onChange(k)}
          className="px-2.5 py-0.5 rounded-full text-[10px] font-bold transition"
          style={{
            background: isActive ? 'linear-gradient(135deg, #4F378B, #7B5EA7)' : 'transparent',
            color: isActive ? '#FFF' : '#B8A8D8'
          }}
          data-testid={`${testIdPrefix}-${k}`}
        >
          {v.label}
        </button>
      );
    })}
  </div>
);

const CenterPanel = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
    className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
  >
    {children}
  </motion.div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CrisisResourcesPanel = ({ zipCode, onBack, onClose }) => {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const cleanZip = (zipCode || '').trim();
    if (!cleanZip || cleanZip.length !== 5 || !/^\d+$/.test(cleanZip)) {
      setLoading(false);
      setError('Enter a 5-digit U.S. ZIP code to see local psychologists.');
      return () => { alive = false; };
    }
    (async () => {
      try {
        const res = await fetch(`${API}/therapists?zip=${cleanZip}`, { cache: 'no-store' });
        const data = await res.json();
        if (!alive) return;
        if (!res.ok) {
          setError(data.detail || 'Could not reach the therapist directory right now.');
        } else {
          setTherapists(data.therapists || []);
        }
      } catch {
        if (alive) setError('Network glitch — please try again in a moment.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [zipCode]);

  return (
  <motion.div
    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="absolute inset-0 overflow-y-auto p-6"
  >
    <button onClick={onBack}
      className="absolute top-3 left-3 flex items-center gap-1 text-xs hover:opacity-80"
      style={{ color: '#B8A8D8' }}
      data-testid="emdr-resources-back"
    >
      <ArrowLeft size={12} /> Back
    </button>
    <button onClick={onClose}
      className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/15"
      style={{ background: "rgba(255,255,255,0.08)", color: "#E8E0F8" }}
      aria-label="Close" data-testid="emdr-resources-close"
    >
      <X size={14} />
    </button>

    <div className="max-w-md mx-auto pt-8 text-left">
      {/* Local psychologists section */}
      <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}>
        🪷 Trauma support near {zipCode || 'you'}
      </div>
      <p className="text-xs italic mb-3" style={{ color: '#B8A8D8' }}>
        Primordeia practitioners listed first, then licensed female psychologists from the U.S. NPI Registry. No psychiatrists. Always verify credentials before booking.
      </p>

      {loading && (
        <div className="text-xs italic mb-4 px-1" style={{ color: '#B8A8D8' }} data-testid="emdr-therapists-loading">
          Searching the registry…
        </div>
      )}
      {!loading && error && (
        <div className="rounded-2xl p-3 mb-3 text-xs" style={{ background: 'rgba(212,165,116,0.12)', border: '1px solid rgba(212,165,116,0.4)', color: '#FFE7C7' }}
          data-testid="emdr-therapists-error">
          {error}
        </div>
      )}
      {!loading && !error && therapists.length === 0 && (
        <div className="rounded-2xl p-3 mb-3 text-xs italic" style={{ background: 'rgba(79,55,139,0.18)', border: '1px solid rgba(123,94,167,0.4)', color: '#E8E0F8' }}
          data-testid="emdr-therapists-empty">
          No psychologists registered at that ZIP code. Try a nearby ZIP, or use the 24/7 lines below for immediate support.
        </div>
      )}
      {!loading && therapists.length > 0 && (
        <ul className="mb-4 space-y-2" data-testid="emdr-therapists-list">
          {therapists.slice(0, 8).map((t) => (
            <li key={t.npi}
              className="rounded-2xl p-3"
              style={{
                background: t.is_premium
                  ? 'linear-gradient(135deg, rgba(212,165,116,0.22), rgba(123,94,167,0.22))'
                  : 'rgba(79,55,139,0.18)',
                border: t.is_premium
                  ? '1px solid rgba(212,165,116,0.6)'
                  : '1px solid rgba(123,94,167,0.4)'
              }}
              data-testid={`emdr-therapist-${t.npi}`}
            >
              {t.is_premium && (
                <div className="text-[9px] font-bold uppercase tracking-[0.18em] mb-1"
                  style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}
                  data-testid={`emdr-therapist-premium-badge-${t.npi}`}>
                  ✦ Primordeia Practitioner
                </div>
              )}
              <div className="text-sm font-bold" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>
                {t.name}{t.credentials ? <span className="font-normal" style={{ color: '#B8A8D8' }}>, {t.credentials}</span> : null}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: '#D4A574' }}>{t.specialty}</div>
              {t.bio && (
                <p className="text-[11px] italic mt-1" style={{ color: '#E8E0F8' }}>{t.bio}</p>
              )}
              {(t.address || t.city) && (
                <div className="text-[11px] mt-1" style={{ color: '#E8E0F8', fontFamily: "'Nunito', sans-serif" }}>
                  {[t.address, t.city, t.state, t.postal_code].filter(Boolean).join(', ')}
                </div>
              )}
              {t.phone && (
                <a href={`tel:${t.phone.replace(/[^\d+]/g, '')}`}
                  className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-bold underline"
                  style={{ color: '#8aa8ff' }}
                  data-testid={`emdr-therapist-phone-${t.npi}`}
                >
                  <Phone size={11} /> {t.phone}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* National crisis lines — always shown */}
      <div className="text-[10px] font-bold uppercase tracking-widest mt-1 mb-1" style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}>
        🌿 U.S. National Crisis Support
      </div>
      <p className="text-xs italic mb-4" style={{ color: '#B8A8D8' }}>
        24/7 lines staffed by trained counsellors.
      </p>

      <div className="rounded-2xl p-4 mb-3" style={{ background: 'rgba(79,55,139,0.18)', border: '1px solid rgba(123,94,167,0.4)' }}>
        <div className="flex items-center gap-2 mb-1.5">
          <Phone size={14} style={{ color: '#8aa8ff' }} />
          <span className="text-sm font-bold" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>988 Suicide &amp; Crisis Lifeline</span>
        </div>
        <ul className="text-xs space-y-1" style={{ color: '#E8E0F8', fontFamily: "'Nunito', sans-serif" }}>
          <li>Call or text: <strong>988</strong></li>
          <li>Chat: <a href="http://988lifeline.org" target="_blank" rel="noreferrer" className="underline" style={{ color: '#8aa8ff' }}>988lifeline.org</a></li>
          <li>Available 24/7, free, confidential</li>
          <li>Connects to trained crisis counsellors</li>
        </ul>
      </div>

      <div className="rounded-2xl p-4 mb-3" style={{ background: 'rgba(79,55,139,0.18)', border: '1px solid rgba(123,94,167,0.4)' }}>
        <div className="flex items-center gap-2 mb-1.5">
          <MessageCircle size={14} style={{ color: '#D4A574' }} />
          <span className="text-sm font-bold" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>Crisis Text Line</span>
        </div>
        <p className="text-xs" style={{ color: '#E8E0F8', fontFamily: "'Nunito', sans-serif" }}>
          Text <strong>HOME</strong> to <strong>741741</strong> &mdash; free, 24/7
        </p>
      </div>

      <div className="rounded-2xl p-4" style={{ background: 'rgba(192,57,43,0.22)', border: '1px solid rgba(192,57,43,0.5)' }}>
        <div className="flex items-center gap-2 mb-1.5">
          <AlertOctagon size={14} style={{ color: '#FFB4A8' }} />
          <span className="text-sm font-bold" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}>Emergency</span>
        </div>
        <p className="text-xs" style={{ color: '#FFE4DD', fontFamily: "'Nunito', sans-serif" }}>
          <strong>911</strong> &mdash; if someone is in immediate danger.
        </p>
      </div>

      <div className="text-center mt-5">
        <button onClick={onClose}
          className="px-5 py-2 rounded-full text-xs font-bold"
          style={{ background: 'rgba(255,255,255,0.1)', color: '#FFF', border: '1px solid rgba(255,255,255,0.2)' }}
          data-testid="emdr-resources-done"
        >
          Return to Primordeia
        </button>
      </div>
    </div>
  </motion.div>
  );
};

export default EMDRBall;
